// Entry point for parsing the table data into the
// correct answer format
export const convertTableDataToCorrectFormat = (
  exportType,
  tableAnswers,
  columns,
  rowLabels
) => {
  switch (exportType) {
    case 'columnsAsLists':
      return getColumnAsListFormat(tableAnswers, columns);
    case 'rowsAsLists':
      if (hasStableColumnKeys(columns)) {
        return getV2RowsAsObjects(tableAnswers, columns);
      }
      return getRowAsListFormat(tableAnswers, rowLabels);
    default:
      break;
  }
};

// For saving the data as "columnsAsLists"
const getColumnAsListFormat = (tableAnswers, columns) => {
  let formattedAnswer = {};

  // Safety checks
  if (
    !tableAnswers ||
    !Array.isArray(tableAnswers) ||
    tableAnswers.length === 0
  ) {
    return formattedAnswer;
  }
  if (!columns || !Array.isArray(columns)) {
    return formattedAnswer;
  }

  let transposeTable = transpose(tableAnswers);

  // Loop over the column Names
  for (let i = 0; i < columns.length; i++) {
    let currCol = columns[i];
    if (!currCol || (!currCol.key && !currCol.columnName)) continue;

    const keyName = getColumnOutputKey(currCol);
    formattedAnswer[keyName] = ['list', ...transposeTable[i]];
  }

  return formattedAnswer;
};

// For saving the data as "rowsAsLists"
const getRowAsListFormat = (tableAnswers, rowLabels) => {
  let formattedAnswer = {};

  // Safety checks
  if (!tableAnswers || !Array.isArray(tableAnswers)) {
    return formattedAnswer;
  }
  if (!rowLabels || !Array.isArray(rowLabels)) {
    return formattedAnswer;
  }

  for (let i = 0; i < tableAnswers.length; i++) {
    const keyName = getRowKeyNameFromLabels(i, rowLabels);
    formattedAnswer[keyName] = ['list', ...tableAnswers[i]];
  }

  return formattedAnswer;
};

// Get the row key name from row labels
// If row label is not empty, return that
// Else return the rowKey
const getRowKeyNameFromLabels = (idx, rowLabels) => {
  try {
    let currRowLabel = rowLabels[idx];
    if (currRowLabel.rowLabel != '') {
      // We have set a row label
      return convertStringToKey(currRowLabel.rowLabel);
    } else {
      // Use default row_key
      return currRowLabel.rowKey;
    }
  } catch (e) {
    return 'row' + idx.toString(); // Default to here
  }
};

export const transpose = (matrix) => {
  try {
    // Safety checks
    if (!matrix || !Array.isArray(matrix) || matrix.length === 0) {
      console.warn(
        'transpose: Invalid matrix input - empty or not an array:',
        matrix
      );
      return [];
    }

    // Check if matrix contains any valid rows
    const hasValidRows = matrix.some(
      (row) => row != null && Array.isArray(row) && row.length > 0
    );
    if (!hasValidRows) {
      console.warn('transpose: Matrix contains no valid rows:', matrix);
      return [];
    }

    if (!matrix[0] || !Array.isArray(matrix[0])) {
      console.warn('transpose: First row is invalid:', matrix[0]);
      return [];
    }

    // Additional safety: filter out any invalid rows and ensure all rows are arrays
    const validMatrix = matrix.filter(
      (row) => row != null && Array.isArray(row) && row.length > 0
    );

    if (validMatrix.length === 0) {
      console.warn('transpose: No valid rows found in matrix:', matrix);
      return [];
    }

    // Find the maximum column length to handle matrices with uneven row lengths
    const maxCols = Math.max(...validMatrix.map((row) => row.length));

    return Array.from({ length: maxCols }, (_, colIndex) =>
      validMatrix.map((row) => {
        // Double check that row is still valid
        if (!row || !Array.isArray(row)) {
          console.warn('transpose: Invalid row found during mapping:', row);
          return null;
        }
        return row[colIndex] || null;
      })
    );
  } catch (error) {
    console.error(
      'transpose: Error occurred during transposition:',
      error,
      'Matrix:',
      matrix
    );
    return [];
  }
};

// Converts a given string to a key
// i.e: "Column Name" ==> "column_name"
export const convertStringToKey = (name) => {
  return name
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '_');
};

const getColumnOutputKey = (column) =>
  column.key || convertStringToKey(column.columnName);

const hasStableColumnKeys = (columns) =>
  Array.isArray(columns) &&
  columns.length > 0 &&
  columns.every((column) => column?.key);

const getV2RowsAsObjects = (tableAnswers, columns) => {
  if (!Array.isArray(tableAnswers)) return [];
  return tableAnswers.map((row) =>
    columns.reduce((result, column, columnIndex) => {
      result[column.key] = Array.isArray(row) ? row[columnIndex] : null;
      return result;
    }, {})
  );
};

// For parsing through fetch table answers from DB
export const parseAnswerData = (
  answerData,
  columns,
  extractFormat,
  rowLabels,
  shown
) => {
  switch (extractFormat) {
    case 'columnsAsLists':
      return parseColumnData(answerData, columns);
    case 'rowsAsLists':
      if (hasStableColumnKeys(columns) && Array.isArray(answerData?.answer)) {
        return parseV2RowData(answerData.answer, columns);
      }
      return parseRowData(answerData, rowLabels, shown);
  }
};

// Used for converting the legacy data that was storing the
// Values as strings
//  To storing it with the defaultVal & calculatedVal Object
const handleLegacyAndNewAnswers = (data) => {
  // Safety check: ensure data is an array
  if (!data || !Array.isArray(data)) {
    return []; // Return empty array if data is undefined or not an array
  }

  return data
    .filter((ans) => ans != 'list')
    .map((ans) => {
      // Either a date time object
      // Or a new answer type object
      if (ans instanceof Object) {
        // If value is null, it is the new answer
        if (ans.value == null) return ans;

        // If the value is an object
        // It is the new datetime object
        if (ans.value instanceof Object) return ans;
        else {
          // Else it is the old datetime objcet
          ans.value = {
            defaultValue: ans.value,
            calculatedValue: ans.value,
          };
          return ans;
        }
      } else
        return {
          defaultValue: ans,
          calculatedValue: ans,
        };
    });
};

const parseV2RowData = (rows, columns) =>
  rows.map((row) =>
    columns.map(
      (column) =>
        handleLegacyAndNewAnswers([row?.[column.key]])[0] ?? {
          defaultValue: '',
          calculatedValue: '',
        }
    )
  );

// Used for converting data saved as "columnsAsLists" from DB
const parseColumnData = (answerData, columns) => {
  let table = [];

  // Safety checks
  if (!columns || !Array.isArray(columns)) {
    return table;
  }
  if (!answerData || !answerData.answer) {
    return table;
  }

  for (let col of columns) {
    if (!col || (!col.key && !col.columnName)) continue;

    let key = getColumnOutputKey(col);

    // Handle legacy and new answer format
    let colData = handleLegacyAndNewAnswers(answerData.answer[key]);
    table.push(colData);
  }

  return transpose(table);
};

// Used for converting data saved as "rowsAsLists" from DB
export const parseRowData = (answerData, rowLabels, shown) => {
  let table = [];

  // Safety check for rowLabels
  if (!rowLabels || !Array.isArray(rowLabels)) {
    return table; // Return empty table if rowLabels is undefined or invalid
  }

  for (let i = 0; i < rowLabels.length; i++) {
    if (shown && i >= shown) break;

    let currRowLabel = rowLabels[i];

    // Safety check for currRowLabel
    if (!currRowLabel) continue;

    let key = currRowLabel.rowKey;

    if (currRowLabel.rowLabel != '')
      key = convertStringToKey(currRowLabel.rowLabel);

    // Safety check for answerData and answerData.answer
    if (!answerData || !answerData.answer || !answerData.answer[key]) {
      // Push empty row if data is missing
      table.push([]);
      continue;
    }

    let rowData = handleLegacyAndNewAnswers(answerData.answer[key]);
    table.push(rowData);
  }

  return table;
};

// Get the table answer keys
export const getTableAnswerKeys = (data) => {
  if (!data) return null;

  let cols = data.data;
  let rowLabels = data.rowLabels;
  let exportType = data.exportType;

  if (exportType === 'columnsAsLists') {
    if (!cols || !Array.isArray(cols)) return null;
    return cols.map(getColumnOutputKey);
  } else if (exportType === 'rowsAsLists') {
    if (hasStableColumnKeys(cols)) return [];
    if (!rowLabels || !Array.isArray(rowLabels)) return null;
    return rowLabels.map((rowLabel) => {
      if (!rowLabel) return 'default_key';
      if (rowLabel.rowLabel != '') {
        return convertStringToKey(rowLabel.rowLabel);
      } else return rowLabel.rowKey || 'default_key';
    });
  }

  return null;
};

// Check to see if a cell is read only
// From the tableCells
// Returns FALSE if table cells do not exist
export const isCellReadOnly = (tableCells, colId, rowIndex) => {
  // Check if exists
  if (tableCells == null) return false;
  if (tableCells[colId] == null) return false;
  if (tableCells[colId][rowIndex] == null) return false;
  if (tableCells[colId][rowIndex].isReadOnly == null) return false;
  return tableCells[colId][rowIndex].isReadOnly;
};

export const isCellDisabled = (tableCells, colId, rowIndex) => {
  // Check if exists
  if (tableCells == null) return false;
  if (tableCells[colId] == null) return false;
  if (tableCells[colId][rowIndex] == null) return false;
  if (tableCells[colId][rowIndex].isDisabled == null) return false;
  return tableCells[colId][rowIndex].isDisabled; // Else return field
};
